import debug from 'debug';
import type { MessageBatcher } from 'message-batcher';
import { serializeError } from 'serialize-error';
import { LogLevels } from '../constants/common';
import { isObject } from '../helpers/common';
import type { LogLevel, LogLevelStr, LogMessageHandler } from '../types/common';

const APP_NAME = 'LiveDigital';
export const BATCH_SIZE = 50;

export interface RecordMeta {
  error?: unknown;
  clientTimestamp: string;
  from_sdk: boolean;
  case?: string;
  channelId?: string;
  applicationId?: string;
  peerId?: string;
  [key: string]: unknown;
}

export interface ClientMeta {
  clUId: string;
  peerId?: string;
  channelId?: string;
  appId?: string;
  [key: string]: unknown;
}

export interface LoggerParams {
  logLevel: LogLevel;
  onLogMessage?: LogMessageHandler;
  sendAnalytics: boolean;
}

export interface LoggerConstructorParams {
  namespace: string;
  logLevel: LogLevel;
  onLogMessage?: LogMessageHandler;
  sendAnalytics: boolean;
  logToRemoteBatcher: MessageBatcher;
}

export type LogRecordMeta = Record<string, unknown> & {
  error?: unknown;
  case?: string;
  channelId?: string;
  applicationId?: string;
  peerId?: string;
};

export interface LogToRemotePayload {
  level: LogLevel;
  message: string;
  meta?: LogRecordMeta;
}

class Logger {
  private readonly _debug: debug.Debugger;

  private readonly _info: debug.Debugger;

  private readonly _warn: debug.Debugger;

  private readonly _error: debug.Debugger;

  readonly #namespace: string;

  readonly #logLevel: LogLevel;

  readonly onLogMessage?: LogMessageHandler;

  readonly #logToRemoteBatcher: MessageBatcher;

  readonly #sendAnalytics: boolean;

  constructor(params: LoggerConstructorParams) {
    const { logLevel, namespace, onLogMessage } = params;
    this.#namespace = namespace;
    this.#logLevel = logLevel;
    this.onLogMessage = onLogMessage;
    this.#logToRemoteBatcher = params.logToRemoteBatcher;
    this.#sendAnalytics = params.sendAnalytics;

    this._debug = debug(APP_NAME).extend('debug').extend(this.#namespace);
    this._info = debug(APP_NAME).extend('info').extend(this.#namespace);
    this._warn = debug(APP_NAME).extend('warn').extend(this.#namespace);
    this._error = debug(APP_NAME).extend('error').extend(this.#namespace);

    // eslint-disable-next-line no-console
    this._debug.log = console.info.bind(console);
    // eslint-disable-next-line no-console
    this._info.log = console.info.bind(console);
    this._warn.log = console.warn.bind(console);
    this._error.log = console.error.bind(console);
  }

  debug(message: string, meta: Record<string, unknown>): void {
    this._debug(message, meta);

    if (this.#logLevel < LogLevels.Debug) {
      return;
    }

    if (this.#sendAnalytics) {
      this.#logToRemoteBatcher.Queue(
        this.#decirculate({ level: 'debug', message, meta: this.#prepareRecordMeta(meta) }),
      );
    }

    this.#log('debug', message, meta);
  }

  info(message: string, meta: Record<string, unknown>): void {
    this._info(message, meta);

    if (this.#logLevel < LogLevels.Info) {
      return;
    }

    if (this.#sendAnalytics) {
      this.#logToRemoteBatcher.Queue(
        this.#decirculate({ level: 'info', message, meta: this.#prepareRecordMeta(meta) }),
      );
    }

    this.#log('info', message, meta);
  }

  warn(message: string, meta: Record<string, unknown>): void {
    this._warn(message, meta);

    if (this.#logLevel < LogLevels.Warn) {
      return;
    }

    if (this.#sendAnalytics) {
      this.#logToRemoteBatcher.Queue(
        this.#decirculate({ level: 'warn', message, meta: this.#prepareRecordMeta(meta) }),
      );
    }

    this.#log('warn', message, meta);
  }

  error(message: string, meta: Record<string, unknown>): void {
    this._error(message, meta);

    if (this.#logLevel < LogLevels.Error) {
      return;
    }

    if (this.#sendAnalytics) {
      this.#logToRemoteBatcher.Queue(
        this.#decirculate({ level: 'error', message, meta: this.#prepareRecordMeta(meta) }),
      );
    }

    this.#log('error', message, meta);
  }

  #log(level: LogLevelStr, message: string, ...args: readonly unknown[]): void {
    if (typeof this.onLogMessage === 'function') {
      this.onLogMessage(message, ...this.#decirculate(args), level);
    }
  }

  #decirculate<Input>(object: Input): Input {
    const seenObjects = new WeakMap<Record<string, unknown>, readonly string[]>();

    function internalDecirculate<T>(value: T, path: readonly string[] = []): T {
      if (!isObject(value)) {
        return value;
      }

      const existingPath = seenObjects.get(value);

      if (existingPath) {
        return `[Circular *${ existingPath.join('.') }]` as unknown as T;
      }

      seenObjects.set(value, path);

      const newValue = (Array.isArray(value) ? [] : {}) as T;

      Object.entries(value).forEach(([key, propValue]) => {
        newValue[key as keyof T] = internalDecirculate(propValue, [...path, key]) as T[keyof T];
      });

      seenObjects.delete(value);

      return newValue;
    }

    return internalDecirculate(object);
  }

  #prepareRecordMeta(metaData?: LogRecordMeta): RecordMeta {
    const meta = metaData ?? {};
    const { error } = meta;

    return {
      ...meta,
      clientTimestamp: new Date().toISOString(),
      from_sdk: true,
      ...(error && typeof error === 'object' ? { error: serializeError(error) } : {}),
      namespace: this.#namespace,
    };
  }
}

export default Logger;
